import { useState } from "react";
import { toast } from "sonner";
import { Check, CheckCircle2, Circle, Download, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApi } from "@/hooks/useApi";
import { api, errMsg, downloadFile } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Section } from "@/components/common/ui-bits";

function ChangeList({ pid, items, onDone }) {
  const [busy, setBusy] = useState(null);
  if (!items.length) return null;
  const open = items.filter((c) => !c.resolved_at).length;
  const toggle = async (c) => {
    setBusy(c.id);
    try { await api.post(`/projects/${pid}/web/changes/${c.id}/resolve`, { resolved: !c.resolved_at }); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="mt-4" data-testid="flow-changes">
      <p className="font-semibold text-slate-900">Cambios pedidos por el cliente <span className="text-xs font-normal text-slate-500" data-testid="flow-changes-count">({open} pendiente{open === 1 ? "" : "s"} de {items.length})</span></p>
      <ul className="mt-2 space-y-2">{[...items].reverse().map((c) => (
        <li key={c.id} className={`rounded-md border px-3 py-2 ${c.resolved_at ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50"}`} data-testid={`flow-change-${c.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className="text-xs text-slate-500">{c.kind === "art" ? "Diseño" : "Revisión final"} · {c.full_name} · {c.at_local}</p>
            {c.id && <Button size="sm" variant={c.resolved_at ? "ghost" : "outline"} disabled={busy === c.id} onClick={() => toggle(c)} data-testid={`flow-change-toggle-${c.id}`}>
              {c.resolved_at ? "Marcar pendiente" : <><Check className="mr-1 h-3.5 w-3.5" />Marcar resuelto</>}</Button>}
          </div>
          <p className={`mt-1 whitespace-pre-line ${c.resolved_at ? "text-slate-500 line-through" : "text-amber-900"}`}>{c.comments}</p>
          {c.resolved_at && <p className="mt-1 text-xs text-emerald-700" data-testid={`flow-change-resolved-${c.id}`}>Resuelto {c.resolved_by ? `por ${c.resolved_by} ` : ""}· {fmtDateTime(c.resolved_at)}</p>}
        </li>))}</ul>
    </div>
  );
}

const STEPS = ["Esperando material", "En diseño", "En construcción", "Revisión final", "Entregado"];

function ActionDialog({ kind, pid, onClose, onDone }) {
  const art = kind === "art";
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  const fields = art ? [["url", "Enlace del diseño (Drive, Canva, etc.) *"], ["notes", "Notas para el cliente"]]
    : [["site_url", "Enlace del sitio *"], ["email_user", "Usuario del correo empresarial"], ["email_guide", "Guía de uso del correo (enlace o texto)"]];
  const go = async () => {
    const req = art ? "url" : "site_url";
    if (!(v[req] || "").trim()) return toast.error(art ? "Escriba el enlace del diseño" : "Escriba el enlace del sitio");
    setBusy(true);
    try {
      const r = await api.post(`/projects/${pid}/web/${art ? "request-art" : "tests-done"}`, v);
      if (r.data.emailed) toast.success("Enviado al cliente por correo"); else toast.info("Listo, pero el correo no se envió: copie el enlace y compártalo con el cliente");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass" data-testid={`flow-dialog-${kind}`}>
        <DialogHeader><DialogTitle className="font-display">{art ? "Enviar arte para aprobación" : "Pruebas completadas"}</DialogTitle></DialogHeader>
        {fields.map(([k, l]) => <div key={k}><label className="field-label">{l}</label>
          <input className="field-input" value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`flow-${k}`} /></div>)}
        {!art && <p className="text-xs text-slate-500">La contraseña del correo no se envía: compártala por WhatsApp o llamada.</p>}
        <div className="flex justify-end"><Button onClick={go} disabled={busy} data-testid="flow-dialog-submit">{art ? "Enviar al cliente" : "Pasar a revisión final"}</Button></div>
      </DialogContent>
    </Dialog>
  );
}

function SendIntake({ pid, resend, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${pid}/web/send-intake`);
      navigator.clipboard.writeText(r.data.link).catch(() => {});
      if (r.data.emailed) toast.success("Formulario enviado al cliente por correo");
      else toast.info("El cliente no tiene correo: el enlace se copió para que lo envíes por WhatsApp");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <Button variant={resend ? "outline" : "default"} onClick={go} disabled={busy} data-testid="flow-send-intake">
    <Send className="mr-2 h-4 w-4" />{resend ? "Reenviar formulario de inicio" : "Enviar formulario de inicio al cliente"}</Button>;
}

export function WebFlowPanel({ pid, onChange, isAdmin }) {
  const { data, reload } = useApi(`/projects/${pid}/web`);
  const [dlg, setDlg] = useState(null);
  const done = () => { setDlg(null); reload(); onChange?.(); };
  if (data && !data.active) return isAdmin && data.web_type ? (
    <Section title="Formulario de inicio de proyecto web" className="lg:col-span-3" testid="web-intake-start">
      <p className="mb-3 text-sm text-slate-600">Envía al cliente un enlace para que llene el formulario y suba su logo y fotos, sin cuenta ni contraseña. Al enviarlo, el proyecto pasa a «Esperando material» y sigue el flujo automático.</p>
      <SendIntake pid={pid} onDone={done} />
    </Section>) : null;
  if (!data) return null;
  const cur = data.status === "En pausa" ? -1 : STEPS.indexOf(data.status);
  return (
    <Section title="Flujo automático de la página" className="lg:col-span-3" testid="web-flow-panel">
      {data.status === "En pausa" && <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="flow-paused">En pausa por falta de respuesta del cliente. Se reanuda cuando el cliente responda.</p>}
      <ol className="flex flex-wrap gap-2 text-xs">{STEPS.map((s, i) => (
        <li key={s} className={`flex items-center gap-1 rounded-full px-3 py-1 font-semibold ${i < cur ? "bg-emerald-50 text-emerald-700" : i === cur ? "bg-blue-700 text-white" : "bg-slate-100 text-slate-500"}`} data-testid={`flow-step-${i}`}>
          {i < cur ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}{s}</li>))}</ol>
      <div className="mt-4 flex flex-wrap gap-2">
        {isAdmin && !data.intake_submitted_at && <SendIntake pid={pid} resend onDone={done} />}
        {data.status === "En diseño" && <Button variant="outline" onClick={() => setDlg("art")} data-testid="flow-request-art">Enviar arte para aprobación</Button>}
        {["En construcción", "Revisión final"].includes(data.status) && <Button variant="outline" onClick={() => setDlg("tests")} data-testid="flow-tests-done">{data.status === "Revisión final" ? "Reenviar revisión final" : "Marcar pruebas completadas"}</Button>}
        {Object.entries(data.links || {}).map(([k, u]) => <Button key={k} variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(u).catch(() => {}); toast.success("Enlace copiado"); }} data-testid={`flow-copy-${k}`}>Copiar enlace {k === "intake" ? "del formulario" : k === "art" ? "de aprobación del arte" : "de aprobación final"}</Button>)}
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2 text-sm">
        <div><p className="font-semibold text-slate-900">Aprobaciones por escrito</p>
          {["art", "final"].map((k) => { const a = data.approvals[k]; return <p key={k} className="text-slate-600" data-testid={`flow-approval-${k}`}>{k === "art" ? "Arte" : "Final"}: {a ? `aprobado por ${a.full_name} · ${a.at_local}` : "pendiente"}</p>; })}
          {data.waiting_since && <p className="mt-2 text-xs text-slate-500">Esperando al cliente desde {fmtDateTime(data.waiting_since)}{data.reminded ? " · recordatorio enviado" : ""}</p>}
        </div>
        <div><p className="font-semibold text-slate-900">Formulario de inicio {data.intake_submitted_at ? `(enviado ${fmtDateTime(data.intake_submitted_at)})` : "(pendiente)"}</p>
          {data.intake.map((x) => <p key={x.label} className="whitespace-pre-line text-slate-600"><b>{x.label}:</b> {x.value}</p>)}
          <div className="mt-2 flex flex-wrap gap-2">{data.files.map((f) => <Button key={f.id} size="sm" variant="outline" onClick={() => downloadFile(`/projects/${pid}/web/files/${f.id}`, null, f.name).catch((e) => toast.error(errMsg(e)))} data-testid={`flow-file-${f.id}`}><Download className="mr-1 h-3.5 w-3.5" />{f.category}: {f.name}</Button>)}</div>
        </div>
      </div>
      <ChangeList pid={pid} items={data.change_requests} onDone={reload} />
      {dlg && <ActionDialog kind={dlg} pid={pid} onClose={() => setDlg(null)} onDone={done} />}
    </Section>
  );
}
